import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'
import HFTextInput from 'components/HFInputs/HFTextInput'
import HFOptionGroup from 'components/HFInputs/HFOptionGroup'
import HFDropdown from 'components/HFInputs/HFDropdown'
import { ForwardingContactController } from 'controllers/TaskManager/TaskSubControllers/ForwardingContactController/ForwardingContactController'
import { ForwardingTaskDataValidationController } from 'controllers/TaskManager/TaskSubControllers/ForwardingTaskDataValidationController/ForwardingTaskDataValidationController'
import { ForwardingMedicalAssistanceController } from 'controllers/TaskManager/TaskSubControllers/ForwardingMedicalAssistanceController/ForwardingMedicalAssistanceController'
import { ForwardingTaskContext } from 'context/ForwardingTask/ForwardingTaskContext'
import {
	booleanOptions,
	trustNetworkFollowingReasons,
} from 'controllers/TaskManager/formOptions'
import { useFetchList } from 'store'
import { useFormContext } from 'react-hook-form'
import { useContext, useEffect, useMemo, useRef } from 'react'
import { Options } from 'models'
import { Segment } from '../../../../utils/enums'

import styles from '../Dialog.module.sass'
import { getBodyPartDefaultRule } from './ForwardingModelRules'
import { usePostalRegionalCheck } from './hooks/usePostalRegionalCheck'
import { useForwardingSuggestions } from './hooks/useForwardingSuggestions'
import { InitialAssessment } from './components/InitialAssessment'
import { ProviderSuggestion } from './components/ProviderSuggestion'
import { HospitalizationFields } from './components/HospitalizationFields'
import { ClientPhoneNumberField } from './components/ClientPhoneNumberField'

export const ForwardingTaskForm = (props) => {
	const { i18n } = useGlobal()
	const { watch, setValue } = useFormContext()
	const {
		accidentType,
		insuranceId,
		processId,
		validateRef,
		setShowWarning,
		requisitionStateId,
		policyNumber,
		showContactForm,
		setShowContactForm,
		contactOpenedByNoInfo,
		setContactOpenedByNoInfo,
		hasClientPhone,
	} = props

	const { establishments } = useContext(ForwardingTaskContext)

	/* CM-185 Exclude Penafiel when AD */
	const EXCLUDED_POLICIES = ['001002016100265', '100265', '1002665', '100625']
	const EXCLUDED_ESTABLISHMENT_ID = '23011'

	const [
		successfulContact,
		contactVictim,
		followOnTrustNetwork,
		notFollowOnTrustNetworkReason,
		isSerious,
		bodyPart,
		postalCode,
		hasStoppedWorking,
		isOnSickLeave,
		bodyPartNoInfo,
		postalCodeNoInfo,
		contactFailedReason,
	] = watch([
		'contact.successfulContact',
		'contact.contactVictim',
		'networkFollow.followOnTrustNetwork',
		'networkFollow.notFollowOnTrustNetworkReason',
		'accident.isSerious',
		'bodyPart.bodyPartName',
		'dataValidation.postalCode',
		'clinicalAssistance.hasStoppedWorking',
		'clinicalAssistance.isOnSickLeave',
		'bodyPart.noInfo',
		'dataValidation.postalCodeNoInfo',
		'contact.contactFailedReason',
	])

	const {
		contents: [fetchedBodyParts],
	} = useFetchList(['/metadata/body_parts'], [Options.fromSimpleObjectJSON])

	const bodyParts = Array.isArray(fetchedBodyParts) ? fetchedBodyParts : []

	// Two different "contact is open" situations:
	//  - contactLocked: any contact flow is open (menu OR "No information").
	//  - initialAssessmentFrozen: ONLY the menu-driven contact freezes the initial
	//    assessment (read-only + its postal check / Trust AI stop at the top).
	//    The "No information" flow keeps the top editable, so its postal check must
	//    keep working — otherwise editing the postal above wouldn't re-validate.
	const contactLocked =
		contactVictim === true || (showContactForm && contactOpenedByNoInfo)
	const initialAssessmentFrozen =
		contactVictim === true && !contactOpenedByNoInfo

	// Postal code regional / central-within-25km check (backend).
	const {
		postalForcesDefaults,
		postalIsRegional,
		postalCentralWithin25km,
		postalCodeValidated,
		postalMissingCoordinates,
		isCheckingPostal,
	} = usePostalRegionalCheck({
		postalCode,
		insuranceId,
		requisitionStateId,
		contactLocked,
	})

	const isWorkRelated = accidentType === Segment.WorkRelated

	// Central + ITA are forced to true when the body part is eyes/teeth OR the
	// postal code is regional / has a central provider within 25km. This
	// automation applies to every segment; only the Trust AI suggestion below is
	// AT-only.
	const bodyPartRule =
		!bodyPartNoInfo && bodyPart !== undefined
			? getBodyPartDefaultRule(bodyPart)
			: null
	// Eyes/teeth force the central provider — used to show an info hint under the
	// body part (mirrors the postal-code hint).
	const bodyPartForcesDefaults = bodyPartRule !== null
	const forcedForwardingDefaults =
		bodyPartRule ??
		(postalForcesDefaults ? { central: true, ita: true } : null)
	const forcesDefaults = forcedForwardingDefaults !== null

	// Any "No information" answer in the initial assessment (body part, postal
	// code or central provider) means we lack the data to forward automatically,
	// so the victim must be called — the contact form is opened automatically.
	// Applies to every segment.
	const hasNoInfoSelected =
		bodyPartNoInfo || postalCodeNoInfo || isSerious === 'no_info'

	// In the "No information" decline flow (operator chose not to contact and
	// picked a reason) a provider is still selected manually, right after the
	// justification.
	const declineProviderReady =
		showContactForm &&
		contactOpenedByNoInfo &&
		contactVictim === false &&
		contactFailedReason !== undefined

	// Postal is "ready" once it is a complete code. We use completeness (not the
	// regional-check `postalCodeValidated` flag) because that flag is frozen once
	// the victim is contacted — so a postal typed in the contact flow would never
	// count.
	const isPostalComplete = /^\d{4}-\d{3}$/.test(postalCode ?? '')

	// Non-AT: the manual provider dropdown opens once body part + postal are
	// resolved and either the defaults are forced (eyes/teeth or regional /
	// central postal) or the central provider question has been answered. ITA is
	// no longer asked in the initial form (always assumed "No").
	const nonWorkRelatedProviderReady =
		!isWorkRelated &&
		(bodyPart !== undefined || bodyPartNoInfo) &&
		(isPostalComplete || postalCodeNoInfo) &&
		(forcesDefaults || isSerious !== undefined)

	// Questions 3 only show after the body part is chosen (or "No
	// information"), the postal code is validated (regional check finished), and
	// defaults are not being forced. This prevents Q3 from flashing and then
	// being hidden if the postal code turns out to be regional. Once it has been
	// answered (`isSerious !== undefined`) keep it visible even while the postal
	// Q3 would unmount/remount and the radio selection would flicker away.
	const showExtraQuestions =
		!forcesDefaults &&
		(bodyPart !== undefined || bodyPartNoInfo) &&
		(postalCodeValidated || postalCodeNoInfo || isSerious !== undefined)

	// Forwarding model provider suggestions.
	const { suggestions, setSuggestions, isLoading } = useForwardingSuggestions(
		{
			insuranceId,
			accidentType,
			requisitionStateId,
			bodyPart,
			bodyPartNoInfo,
			postalCode,
			postalCodeNoInfo,
			isSerious,
			hasStoppedWorking,
			isOnSickLeave,
			forcedForwardingDefaults,
			postalForcesDefaults,
			contactLocked,
			// Never run the model when there is no model-ready data to run it on:
			//  - the contact was opened by a "No information" answer → the whole
			//    flow is manual-only (we lack the data the model needs);
			//  - the operator declined to contact (contactVictim === false);
			//  - the injured is not followed in the Trust network.
			skipSuggestions:
				(contactOpenedByNoInfo && successfulContact !== '1') ||
				contactVictim === false ||
				followOnTrustNetwork === false,
		}
	)

	/* CM-185 Exclude Penafiel */
	const filteredEstablishments = useMemo(() => {
		if (
			accidentType === Segment.SportsAccident &&
			EXCLUDED_POLICIES.includes(policyNumber)
		) {
			return establishments?.filter(
				(e) => e.value !== EXCLUDED_ESTABLISHMENT_ID
			)
		}
		return establishments
	}, [establishments, accidentType, policyNumber])

	// Body part "No information" clears the previous selection and suggestion so
	// the model does not run with a stale body part.
	useEffect(() => {
		if (bodyPartNoInfo) {
			setValue('bodyPart.bodyPartName', undefined)
			setSuggestions(null)
		}
		// eslint-disable-next-line
	}, [bodyPartNoInfo])

	// Postal code "No information" clears the postal value and suggestion so the
	// model does not run with a stale / partial postal code.
	useEffect(() => {
		if (postalCodeNoInfo) {
			setValue('dataValidation.postalCode', undefined)
			setSuggestions(null)
		}
		// eslint-disable-next-line
	}, [postalCodeNoInfo])

	// Selecting any "No information" (a checkbox or the central-provider "no
	// information" option) opens the contact form with "Yes" pre-selected, same
	// as the "Contact injured" menu action; without data we have to call the
	// victim. Flagged as no-info-origin so declining to contact shows the
	// justification flow instead of closing.
	useEffect(() => {
		if (hasNoInfoSelected && !contactOpenedByNoInfo) {
			setShowContactForm?.(true)
			setContactOpenedByNoInfo?.(true)
			setValue('contact.contactVictim', true)
		}
		// eslint-disable-next-line
	}, [hasNoInfoSelected])

	// "No information" contact flow is open — nothing there should clear it.
	const lastIsSeriousRef = useRef(isSerious)
	useEffect(() => {
		if (isSerious !== undefined) {
			lastIsSeriousRef.current = isSerious
		} else if (
			contactOpenedByNoInfo &&
			lastIsSeriousRef.current !== undefined
		) {
			setValue('accident.isSerious', lastIsSeriousRef.current)
		}
		// eslint-disable-next-line
	}, [isSerious, contactOpenedByNoInfo])

	// the contact flow (after a successful call) without closing/resetting.
	// contactVictim can still be `undefined` here (e.g. the contact question
	// only registers once the contact block mounts) — only a real decline
	// (`false`) or an ongoing call (`successfulContact` answered) counts as
	// engaged.
	const contactNotEngaged =
		contactVictim !== false && successfulContact === undefined
	useEffect(() => {
		if (contactOpenedByNoInfo && !hasNoInfoSelected && contactNotEngaged) {
			setShowContactForm?.(false)
			setContactOpenedByNoInfo?.(false)
			setValue('contact.contactVictim', undefined)
		}
		// eslint-disable-next-line
	}, [hasNoInfoSelected])

	// Choosing "No" in "Do you wish to contact the victim?":
	//  - Menu origin: gracefully close the contact block (no clearing, no
	//    justification) so a mistaken open can be undone.
	//  - No-information origin: keep it open so the operator must justify why the
	//    victim is not being contacted (handled inside ForwardingContactController).
	useEffect(() => {
		if (
			showContactForm &&
			contactVictim === false &&
			!contactOpenedByNoInfo
		) {
			setShowContactForm?.(false)
			setValue('contact.contactVictim', undefined)
		}
		// eslint-disable-next-line
	}, [contactVictim])

	// Entering the contact flow resets the initial-form suggestion — the provider
	// panel only reappears after the data is re-answered during the call (the
	// triage "central provider" answer was cleared when the contact was revealed).
	useEffect(() => {
		if (contactLocked) {
			setSuggestions(null)
		}
		// eslint-disable-next-line
	}, [contactLocked])

	// Forced defaults (eyes/teeth or regional/central postal) clear Q3
	useEffect(() => {
		// Skip while the initial assessment is frozen — the central/ITA answers
		// are then owned by the contact flow and must not be cleared.
		if (forcesDefaults && !contactLocked) {
			setValue('accident.isSerious', undefined)
			setValue('clinicalAssistance.hasStoppedWorking', undefined)
		}
		// eslint-disable-next-line
	}, [forcesDefaults])

	// "Hospitalization" chosen as the non-follow reason drives the hospitalized
	// flow (temporary provider + hospitalization fields), reusing the existing
	// logic keyed off clinicalAssistance.injuredHospitalized.
	useEffect(() => {
		setValue(
			'clinicalAssistance.injuredHospitalized',
			notFollowOnTrustNetworkReason === 4
		)
		// eslint-disable-next-line
	}, [notFollowOnTrustNetworkReason])

	useEffect(() => {
		props.onResolverDisabled?.(false)
		// eslint-disable-next-line
	}, [])

	// "Not in need of assistance" (reason 1) ends the contact flow right after
	// the Trust-network follow-up question — the remaining questions (body part,
	// medical assistance, provider) no longer apply.
	const flowEndsAtNetworkFollow =
		followOnTrustNetwork === false && notFollowOnTrustNetworkReason === 1

	// Not following in the Trust network means the injured is being handled
	// outside it (treatment outside network / hospitalization), so no provider is
	// selected and the model must not run — the reason itself is the outcome.
	const isNetworkFollowDeclined = followOnTrustNetwork === false
	const hideProviderForNetworkDecline =
		isNetworkFollowDeclined &&
		(notFollowOnTrustNetworkReason === 2 ||
			notFollowOnTrustNetworkReason === 4)

	const providerSuggestion = (
		<ProviderSuggestion
			suggestions={suggestions}
			isLoading={isLoading}
			filteredEstablishments={filteredEstablishments}
			processId={processId}
			validateRef={validateRef}
			onProviderSelected={props.onProviderSelected}
			setShowWarning={setShowWarning}
			requisitionStateId={requisitionStateId}
			accidentType={accidentType}
			showManualProvider={
				nonWorkRelatedProviderReady || declineProviderReady
			}
			// Declining to contact the victim → manual provider only, never the
			// Trust AI suggestion (regardless of any lingering suggestion state).
			manualProviderOnly={contactVictim === false}
		/>
	)

	return (
		<>
			{!hasClientPhone && <ClientPhoneNumberField />}

			<InitialAssessment
				bodyParts={bodyParts}
				bodyPartNoInfo={bodyPartNoInfo}
				bodyPartForcesDefaults={bodyPartForcesDefaults}
				postalCodeNoInfo={postalCodeNoInfo}
				showExtraQuestions={showExtraQuestions}
				postalMissingCoordinates={postalMissingCoordinates}
				postalIsRegional={postalIsRegional}
				postalCentralWithin25km={postalCentralWithin25km}
				isCheckingPostal={isCheckingPostal}
				accidentType={accidentType}
				// Freeze the top form only when the contact was opened from the
				// menu. When it was opened by a "No information" answer the operator
				// must still be able to correct the initial assessment above.
				disabled={initialAssessmentFrozen}
			/>

			{/* Before the victim is contacted the suggestion sits under the
			    initial assessment; once contacted it moves to the end of the
			    contact flow (rendered below). */}
			{!showContactForm && providerSuggestion}

			{showContactForm && (
				<ForwardingContactController
					showJustification={contactOpenedByNoInfo}
				/>
			)}

			{/* No-information decline flow: pick a provider after the reason. */}
			{declineProviderReady && providerSuggestion}
			{showContactForm && contactVictim && successfulContact === '1' && (
				<>
					<ForwardingTaskDataValidationController
						accidentType={accidentType}
					/>
					{/* 1. Breve descrição dos acontecimentos */}
					<fieldset>
						<VStack className={styles['option-group-list']}>
							<VStack className={styles['form-input']}>
								<h5>{i18n`Brief description of events`}</h5>
								<HFTextInput
									name='injuryDescription'
									multiline={true}
									rows={5}
									noResize
								/>
							</VStack>
						</VStack>
					</fieldset>

					{/* 2-3. Seguimento na rede Trust (+ motivo, se "Não") */}
					<fieldset>
						<VStack gap='xsmall'>
							<VStack>
								<h5>{i18n`Following in Trust network?`}</h5>
								<HFOptionGroup
									className={`${styles['option-group']} ${styles['--horizontal-flex']}`}
									name='networkFollow.followOnTrustNetwork'
									type='boolean'
									options={booleanOptions}
								/>
							</VStack>
							{followOnTrustNetwork !== undefined &&
								!followOnTrustNetwork && (
									<>
										<VStack
											className={styles['form-input']}
										>
											<h5>{i18n`Reason`}</h5>
											<HFDropdown
												name='networkFollow.notFollowOnTrustNetworkReason'
												options={
													trustNetworkFollowingReasons ||
													[]
												}
												optionsAlign='right'
												searchable={true}
												noData={i18n`No results`}
											/>
										</VStack>
										{notFollowOnTrustNetworkReason ===
											4 && <HospitalizationFields />}
									</>
								)}
						</VStack>
					</fieldset>

					{/* 4. Parte do corpo */}
					{!flowEndsAtNetworkFollow && (
						<>
							<fieldset>
								<VStack className={styles['option-group-list']}>
									<VStack className={styles['form-input']}>
										<h5>{i18n`Body part`}</h5>
										<HFDropdown
											className={`${styles['option-group']} ${styles['--horizontal-flex']}`}
											optionsAlign='right'
											searchable={true}
											name='bodyPart.bodyPartName'
											noData={i18n`No results`}
											options={bodyParts || []}
											type='number'
										/>
									</VStack>
								</VStack>
							</fieldset>

							{/* 5-7. Teve assistência médica → parou atividade → prestador
					    central */}
							<ForwardingMedicalAssistanceController
								accidentType={accidentType}
							/>

							{/* Trust AI suggestion, recalculated from the data validated
					    during the contact flow. Hidden when the injured is not
					    followed in the Trust network due to hospitalization or
					    treatment outside the network. */}
							{!hideProviderForNetworkDecline &&
								providerSuggestion}
						</>
					)}
				</>
			)}

			<fieldset className={styles['observation-container']}>
				<h5>{i18n`Observations`}</h5>
				<HFTextInput
					name='observations'
					multiline={true}
					rows={5}
					noResize
				/>
			</fieldset>
		</>
	)
}
